import React, { useState, useEffect } from 'react';
import SearchableSelect from './SearchableSelect';
import { useAuthenticatedApi } from '@/utils/callApi';

/**
 * Reusable component for selecting roles (surveys, reports, etc.)
 * @param {Array} selectedRoles - Array of selected role IDs
 * @param {Function} onRolesChange - Callback when roles change
 * @param {string} title - Optional title for the section (default: "Roles")
 * @param {string} description - Optional description text
 * @param {boolean} required - Whether roles are required
 */
export default function RolesSelector({ 
  selectedRoles = [], 
  onRolesChange,
  title = "Roles",
  description = "Select roles to assign",
  required = true
}) {
  const { secureHelpers } = useAuthenticatedApi();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load roles from API
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const data = await secureHelpers.get('/api/roles');
        setRoles(Array.isArray(data) ? data : []);
        console.log('Roles loaded:', data);
      } catch (error) {
        console.error('Failed to load roles:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRoles();
  }, []);

  const removeRole = (roleIdToRemove) => {
    const updatedRoles = selectedRoles.filter(roleId => roleId !== roleIdToRemove);
    onRolesChange(updatedRoles);
  };

  const addRole = (roleId) => {
    // SearchableSelect now passes just the ID by default
    if (roleId && !selectedRoles.includes(roleId)) {
      onRolesChange([...selectedRoles, roleId]);
    }
  };

  return (
    <div className="card-container mb-4 p-3">
      <h4>
        {title}
        {required && <span className="text-danger"> *</span>}
      </h4>
      {loading ? (
        <div className="text-muted d-flex align-items-center gap-2">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Loading roles...
        </div>
      ) : (
        <>
          {/* Show selected roles with name and description */}
          {selectedRoles.length > 0 && (
            <div className="mb-3">
              {selectedRoles.map(roleId => {
                const role = roles.find(r => r.id === roleId);
                if (!role) return null; // Skip if role not found
                return (
                  <div 
                    key={roleId}
                    className="d-flex justify-content-between align-items-center p-2 mb-2 border rounded"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    <div className="d-flex flex-column flex-grow-1">
                      <div className="d-flex flex-row gap-5">
                        <div style ={{ fontSize: "var(--font-heading-sm)", color: "#2e1719"}}>{role.name}</div>
                        <div style ={{ fontSize: "var(--font-base)", color: "#666" }}>{role.description}</div>
                        <div style={{ fontSize: "var(--font-base)", color: "#666" }}>ID: {role.id}</div>
                      </div>
                      
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRole(roleId)}
                      className="btn btn-sm btn-link text-danger p-0 ms-3"
                      style={{ fontSize: '1.5rem', lineHeight: '1' }}
                      title="Remove role"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
      <p className="mb-3" style={{ fontSize: "1.1rem" }}>{description}</p>
      
          {/* SearchableSelect for adding roles */}
          <SearchableSelect
            options={roles.filter(role => !selectedRoles.includes(role.id))}
            value={""}
            onChange={addRole}
            placeholder="Select role..."
            getLabel={(role) => role.name}
            getValue={(role) => role.id}
          />
        </>
      )}
    </div>
  );
}
